// #genai Phase 2 — small payment-method brand mark shown on each saved-method row.
// Pixel-matched to Figma 183:31478 / 239:7101 / 239:7271 (35x24 white box, 1px #E4E4E7 border, radius 4).
// The Figma uses real brand assets (Visa wordmark, Mastercard interlocking circles, Phosphor "bank"
// glyph) — this file ships compact inline SVGs that visually mirror those marks since the repo has
// no brand-logo image assets.
import React from 'lib/react';
import PropTypes from 'prop-types';
import { makeStyles, EVAccountBalance } from 'lib/components';

const useStyles = makeStyles(() => ({
  box: {
    boxSizing: 'border-box',
    width: '35px',
    height: '24px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E4E4E7',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  // #genai Phosphor "bank" glyph in Figma is a filled Greek-temple silhouette — Material's
  // AccountBalance is visually equivalent at this size (~16px).
  bankGlyph: {
    width: '16px',
    height: '16px',
    color: '#1B202C',
  },
  cardGlyph: {
    width: '18px',
    height: '18px',
    color: '#61687D',
  },
  // #genai Visa wordmark — uses the official Visa-blue colorway. The italic "VISA" letters are
  // drawn directly with SVG <text> so we don't ship a binary asset; the result reads as the
  // Visa logo at the 35x24 pill size used in Figma 183:31478.
  visaSvg: {
    width: '28px',
    height: '12px',
  },
  amex: {
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontWeight: 700,
    fontSize: '7px',
    lineHeight: 1,
    letterSpacing: '0.2px',
    color: '#FFFFFF',
    backgroundColor: '#1F72CD',
    borderRadius: '2px',
    padding: '2px 3px',
  },
  discover: {
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontWeight: 700,
    fontSize: '6.5px',
    letterSpacing: '0.2px',
    color: '#231F20',
  },
  discoverDot: {
    color: '#F76B1C',
  },
  mastercardSvg: {
    width: '22px',
    height: '14px',
  },
}));

function VisaLogo({ className }) {
  // #genai Figma uses a raster of the Visa wordmark; this SVG reproduces it close enough at 35x24.
  return (
    <svg
      className={className}
      viewBox="0 0 56 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <text
        x="0"
        y="19"
        fontFamily="Arial, Helvetica, sans-serif"
        fontStyle="italic"
        fontWeight="900"
        fontSize="22"
        letterSpacing="0.5"
        fill="#1A1F71"
      >
        VISA
      </text>
    </svg>
  );
}
VisaLogo.propTypes = { className: PropTypes.string };
VisaLogo.defaultProps = { className: '' };

function MastercardLogo({ className }) {
  // #genai Iconic interlocking-circles Mastercard mark. Left circle is red (#EB001B), right is
  // orange (#F79E1B); the overlap uses #FF5F00 (the Mastercard "overlap" color) so the mark
  // matches the Figma asset at the 35x24 pill size.
  return (
    <svg
      className={className}
      viewBox="0 0 32 20"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="12" cy="10" r="8" fill="#EB001B" />
      <circle cx="20" cy="10" r="8" fill="#F79E1B" />
      <path
        d="M16 4.2a8 8 0 0 0 0 11.6 8 8 0 0 0 0-11.6z"
        fill="#FF5F00"
      />
    </svg>
  );
}
MastercardLogo.propTypes = { className: PropTypes.string };
MastercardLogo.defaultProps = { className: '' };

function CardGlyph({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
    </svg>
  );
}
CardGlyph.propTypes = { className: PropTypes.string };
CardGlyph.defaultProps = { className: '' };

function PaymentMethodBrandIcon({ type, brand }) {
  const classes = useStyles();

  const renderContent = () => {
    if (type === 'ach') {
      return <EVAccountBalance className={classes.bankGlyph} />;
    }
    switch ((brand || '').toLowerCase()) {
      case 'visa':
        return <VisaLogo className={classes.visaSvg} />;
      case 'mastercard':
        return <MastercardLogo className={classes.mastercardSvg} />;
      case 'amex':
        return <span className={classes.amex}>AMEX</span>;
      case 'discover':
        return (
          // #genai `DISC<dot>VER` is expressed as three JSX children with explicit string
          // literals so React emits exactly "DISC•VER" without any whitespace between the
          // three nodes (Sonar S6772: ambiguous spacing). The string literals must be wrapped
          // in braces to remove the ambiguity, and the `react/jsx-curly-brace-presence` rule
          // is disabled here for the two string children so both lint rules are satisfied.
          <span className={classes.discover}>
            {/* eslint-disable-next-line react/jsx-curly-brace-presence */}
            {'DISC'}
            <span className={classes.discoverDot}>•</span>
            {/* eslint-disable-next-line react/jsx-curly-brace-presence */}
            {'VER'}
          </span>
        );
      default:
        return <CardGlyph className={classes.cardGlyph} />;
    }
  };

  return <div className={classes.box}>{renderContent()}</div>;
}

PaymentMethodBrandIcon.propTypes = {
  type: PropTypes.oneOf(['ach', 'card']).isRequired,
  brand: PropTypes.string,
};

PaymentMethodBrandIcon.defaultProps = {
  brand: '',
};

export default PaymentMethodBrandIcon;
